import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { storage } from '@abyss/web/tools/storage';
import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Modal } from '@abyss/web/ui/Modal';
import { createRef, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { PortalContext } from '../../context/PortalContext';
import { SearchFilterContext } from '../../context/SearchFilterContext';
import { redirectToGuestSignInSchedulingCallback } from '../../frontends/GuestExperience/Guest.utils';
import { useAnnexEncryptedPayload } from '../../hooks/useArcade';
import { useHsidUuid } from '../../hooks/useHsidUuid';
import { useLagoon } from '../../hooks/useLagoon';
import { usePingFederateEncryptedPayload } from '../../hooks/usePingFederate';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { usePCPStore } from '../../store/usePCPStore';
import { PCPSearchStore } from '../../store/usePCPStore/pcpStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { shortenZipCode } from '../../utils/providerDetails.utils';
import { capitalizeFirstLetters } from '../../utils/providerNames.utils';
import {
  getDocAsapLagoonKeys,
  getLagoonConfigValue,
} from '../../utils/providerSearch.utils';
import { getDocAsapTargetUrl } from '../../utils/scheduling.utils';
import { getCurrentMember, getLoggedInMember } from '../../utils/user.utils';
import { Constants, FilterCollectionModel, ProviderTypes } from '../Constants';
import { phoneOnly } from '../ConstantsStyles';
import { phoneTextStyles } from '../DataCard/Phone';
import { HiddenFormSubmission } from '../ScheduleModal/ScheduleModal.style';
import { ScheduleModalHiddenForm } from '../ScheduleModal/ScheduleModalHiddenForm';
import { getFeatureFlag } from '../Utils';
import {
  CallContainer,
  CancelButton,
  ModalTitle,
  ResponsiveText,
  ScheduleButton,
  ScheduleContainer,
} from './CareTeamProviderCard.styled';

export const ScheduleModal = ({
  isOpen,
  onClose,
  providerLocations,
  npi,
  providerId,
  isTieredDataCard,
  providerType,
  showDirection = false,
}) => {
  const mobileScreen = useMediaQuery(phoneOnly);
  const [selectedId, setSelectedId] = useState(
    providerLocations[0]?.locationId
  );
  const currentMember = getCurrentMember();
  const loggedInMember = getLoggedInMember();
  const { t } = useTranslation();
  const hiddenSubmit = createRef<HTMLButtonElement>();
  const [encryptedPayload, setEncryptedPayload] = useState<string>('');
  const [memberId] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MEMBER_ID,
    0
  );
  const allConfigs = useLagoon('config');
  const featureFlags = useLagoon('feature-flags')();
  const { portalData } = useContext(PortalContext);
  const { searchFilters } = useContext(SearchFilterContext);
  const portalName = portalData?.portalName;

  // Determine if BCC filter is selected
  const isBccFilterSelected =
    searchFilters[FilterCollectionModel.PRIORITY_SCHEDULING]?.value === true;

  const docAsapPartnerCodeKey = getDocAsapLagoonKeys(
    'DOCASAP_PARTNER_CODE',
    portalName,
    isBccFilterSelected
  );
  const docAsapChannelIdKey = getDocAsapLagoonKeys(
    'DOCASAP_CHANNEL_ID',
    portalName,
    isBccFilterSelected
  );
  const callPingFederate = getFeatureFlag(featureFlags, 'PING_FEDERATE');
  const partnerSpId = getLagoonConfigValue(allConfigs, 'PARTNER_SP_ID');
  const pfidAdapterId = getLagoonConfigValue(allConfigs, 'PFID_ADAPTER_ID');
  const vendorId = getLagoonConfigValue(allConfigs, 'DOCASAP_VENDOR_ID');
  const vsid = getLagoonConfigValue(allConfigs, 'PFID_VS_ID');
  const docAsapUrl = getLagoonConfigValue(allConfigs, 'DOCASAP_URL');
  const docAsapPartnerCode = getLagoonConfigValue(
    allConfigs,
    docAsapPartnerCodeKey
  );
  const docAsapCredentialCode = getLagoonConfigValue(
    allConfigs,
    'DOCASAP_CREDENTIAL_CODE'
  );
  const docAsapChannelId = getLagoonConfigValue(
    allConfigs,
    docAsapChannelIdKey
  );
  const isGuestUser = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.IS_GUEST
  );
  const hsidUuid = useHsidUuid();
  const ssoOutUrl = callPingFederate
    ? getLagoonConfigValue(allConfigs, 'PF_SSOOUT_URL')
    : getLagoonConfigValue(allConfigs, 'FPC_SSOOUT_URL');
  const [, getPayload] = useAnnexEncryptedPayload({
    onCompleted: (result) => {
      setEncryptedPayload(result?.data?.annexEncryptedPayload?.payload || '');
    },
  });

  const [, getPingFederatePayload] = usePingFederateEncryptedPayload({
    onCompleted: (result) => {
      setEncryptedPayload(result?.data?.pingFederate?.ref || '');
    },
  });
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const pcpStore = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      choosePCP: state.pcpSearchState.choosePCP,
    }))
  );

  const choosePCPFlag = isGuestUser ? pcpStore.choosePCP : false;
  const selectedPracticeId = providerLocations?.find(
    (location) => location?.locationId === selectedId
  )?.docasapPracticeId;

  // Iterate over practiceIds and generate URLs for each practiceId
  const ssoOutTargetUrl = getDocAsapTargetUrl(
    docAsapUrl,
    docAsapPartnerCode,
    docAsapCredentialCode,
    docAsapChannelId,
    npi,
    selectedPracticeId
  );
  useEffect(() => {
    if (encryptedPayload) {
      hiddenSubmit.current?.click();
    }
  }, [encryptedPayload]);

  const sectionType =
    providerType === ProviderTypes.PRACTITIONER
      ? Constants.SEARCH_TYPES.PROVIDER
      : Constants.SEARCH_TYPES.FACILITY;
  const handleScheduleClick = (selectedId) => {
    if (isGuestUser) {
      redirectToGuestSignInSchedulingCallback(
        portalData?.portalConfig?.basePath,
        portalData?.portalConfig?.signInUrl || '',
        chipStore,
        providerId,
        selectedId,
        showDirection,
        isTieredDataCard,
        sectionType,
        choosePCPFlag,
        portalData?.portalConfig?.language
      );
    }
    //Added feature flag check to call the correct hook
    if (!isGuestUser) {
      callPingFederate
        ? getPingFederatePayload({
            variables: {
              pingFederatePayload: {
                subject: loggedInMember?.rallyId,
                firstName: currentMember?.demographics?.name.firstName,
                lastName: currentMember?.demographics?.name.lastName,
                dob: currentMember?.demographics?.dateOfBirth,
                groupNumber: currentMember?.policyNumber,
                memberId: memberId.toString(),
                gender: currentMember?.demographics?.gender,
                state: currentMember?.demographics?.address?.[0]?.state,
                zipCode: currentMember?.demographics?.address?.[0]?.zipCode,
                city: currentMember?.demographics?.address?.[0]?.city,
                address1: currentMember?.demographics?.address?.[0]?.address1,
              },
              ...(hsidUuid && { userId: hsidUuid }),
            },
          })
        : getPayload();
    }
  };
  const typeaheadState = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      initiateDocasapSession: state.typeaheadSearchStore.initiateDocasapSession,
    }))
  );
  useEffect(() => {
    if (typeaheadState?.initiateDocasapSession) {
      handleScheduleClick(selectedId);
    }
  }, [typeaheadState?.initiateDocasapSession]);
  return (
    <Modal
      data-auto-testid="schedule-modal"
      data-testid="schedule-modal"
      isOpen={isOpen}
      onClose={onClose}
      title={t('CARE_TEAM.CARE_TEAM_SCHEDULE_MODAL.HEADING')}
      titleAlign="center"
    >
      <Modal.Section>
        <Flex
          direction="column"
          style={{ width: '90%', justifySelf: 'center' }}
        >
          {providerLocations
            .filter((clinic) => clinic?.docasapPracticeId)
            .map((clinic) => {
              const isSelected = selectedId === clinic.locationId;
              return (
                <CallContainer
                  data-auto-testid={`schedule-container-${clinic.locationId}`}
                  data-testid={`schedule-container-${clinic.locationId}`}
                  hasMultiple={providerLocations.length > 1}
                  key={clinic.id}
                  onClick={() => setSelectedId(clinic.locationId)}
                >
                  <ScheduleContainer
                    alignItems="center"
                    data-auto-testid={`data-card-clinic-name-${clinic.locationId}`}
                    data-testid={`data-card-clinic-name-${clinic.locationId}`}
                    direction="row"
                    isSelected={isSelected}
                    justify="space-between"
                  >
                    <Flex direction="column" style={{ flex: 1, width: '80%' }}>
                      {clinic?.providerGroupsAffiliations?.[0] && (
                        <ModalTitle css={{ fontWeight: '700' }}>
                          {capitalizeFirstLetters(
                            clinic?.providerGroupsAffiliations[0]?.value
                          )}
                        </ModalTitle>
                      )}
                      <ModalTitle css={{ fontWeight: '500', color: '#4B4D4F' }}>
                        {clinic?.address?.line} {clinic?.address?.city},{' '}
                        {clinic?.address?.state}{' '}
                        {shortenZipCode(clinic?.address?.postalCode)}
                      </ModalTitle>
                    </Flex>
                    {isSelected && (
                      <IconMaterial
                        color="#196ECF"
                        data-auto-testid="selected-icon"
                        data-testid="selected-icon"
                        icon="check_circle"
                        size={24}
                        style={{ alignSelf: 'flex-start' }}
                      />
                    )}
                  </ScheduleContainer>
                  {clinic?.acceptingNewPatients ? (
                    <Flex
                      alignItems="center"
                      data-auto-testid={`data-card-phone-tag-${clinic.locationId}`}
                      data-testid={`data-card-phone-tag-${clinic.locationId}`}
                      direction="row"
                      key={`data-card-phone-tag-${clinic.locationId}`}
                      style={{ marginTop: '3px' }}
                    >
                      <IconMaterial
                        aria-label="accepting new patients"
                        color="$success1"
                        data-auto-testid={`data-card-phone-icon-${clinic.locationId}`}
                        data-testid={`data-card-phone-icon-${clinic.locationId}`}
                        icon="check"
                        isScreenReadable
                        size={mobileScreen ? 18 : 24}
                        title="accepting new patients"
                      />
                      <ResponsiveText
                        color="#323333"
                        css={phoneTextStyles}
                        data-auto-testid={`accept-new-patients-${clinic.locationId}`}
                        data-testid={`accept-new-patients-${clinic.locationId}`}
                        fontWeight="500"
                        key={`accept-new-patients-${clinic.locationId}`}
                      >
                        {t('Accepting new patients')}
                      </ResponsiveText>
                    </Flex>
                  ) : null}
                </CallContainer>
              );
            })}
          <div
            data-auto-testid="schedule-modal-button-container"
            data-testid="schedule-modal-button-container"
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              marginTop: '16px',
            }}
          >
            <CancelButton
              aria-label="Cancel"
              data-auto-testid="cancel-button"
              data-testid="cancel-button"
              onClick={onClose}
              variant="outline"
            >
              {t('CARE_TEAM.CARE_TEAM_SCHEDULE_MODAL.CANCEL')}
            </CancelButton>
            <ScheduleButton
              after={<IconMaterial icon="open_in_new" />}
              aria-label="Schedule Now"
              data-auto-testid="schedule-button"
              data-testid="schedule-button"
              disabled={!selectedPracticeId}
              onClick={() => {
                handleScheduleClick(selectedId);
              }}
            >
              {t('CARE_TEAM.CARE_TEAM_SCHEDULE_MODAL.SCHEDULE_NOW')}
            </ScheduleButton>
            <HiddenFormSubmission
              action={ssoOutUrl}
              data-testid="docasap-form"
              method={callPingFederate ? 'get' : 'post'}
              target="_blank"
            >
              <ScheduleModalHiddenForm
                {...{
                  callPingFederate,
                  encryptedPayload,
                  partnerSpId,
                  pfidAdapterId,
                  ssoOutTargetUrl,
                  ssoOutUrl,
                  vendorId,
                  vsid,
                }}
              />
              <button ref={hiddenSubmit} type="submit" />
            </HiddenFormSubmission>
          </div>
        </Flex>
      </Modal.Section>
    </Modal>
  );
};
